import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Plan2PlateDrawer,
  Plan2PlateDrawerHeader,
  usePlan2PlateDisclosure,
} from "./Plan2PlateDrawer";
import { Input } from "./ui/Input";
import { Textarea } from "./ui/Textarea";
import { Button } from "./ui/Button";
import { Card } from "./ui/Card";
import { SectionLabel } from "./ui/SectionLabel";
import { SmallIconButton } from "./SmallIconButton";
import { SearchForFoodDrawer } from "./SearchForFoodDrawer";
import { EditFoodDrawer } from "./EditFoodDrawer";
import { UnitAmount } from "./UnitAmount";
import { DrawerHistoryContext } from "../contexts";
import { Spinner } from "./ui/Spinner";
import { Counter } from "./ui/Counter";
import { LookupSkeleton } from "./ui/LookupSkeleton";
import { useToast } from "./ui/Toast";
import {
  convertToDish,
  DishModificationDto,
  ExtractIngredientsResponseDto,
  ExtractIngredientsResponseListDto,
  getIngredients,
  getIngredientsFromImage,
  getIngredientsFromText,
  IngredientMappingDto,
  StepIngredientCreateDto,
  suggestFood,
} from "../api";
import Food from "../data/food";
import { FoodUnit } from "../data/foodUnit";
import { DishModification } from "../data/dishModification";
import {
  formatExtractedQuantity,
  matchExtractedIngredient,
} from "../utils/dishImportUtils";
import { formatFoodDescription } from "../utils/foodUtils";
import { cn } from "../lib/utils";

type DishImportStep = "input" | "loading" | "preview";

type IngredientMapping = {
  extracted: ExtractIngredientsResponseDto;
  food?: Food;
  foodUnitId?: number;
  amount: number;
  ignored: boolean;
  suggestionLoading?: boolean;
  suggestedFood?: Food;
  suggestionSource?: string;
  suggestedFoodUnitId?: number;
  suggestedAmount?: number;
};

/** Parallele KI-Vorschlagsanfragen begrenzen, um das Backend nicht zu fluten. */
const SUGGESTION_CONCURRENCY = 3;

async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<void>,
) {
  let next = 0;
  const workers = Array.from(
    { length: Math.min(limit, items.length) },
    async () => {
      while (next < items.length) {
        const index = next++;
        await worker(items[index], index);
      }
    },
  );
  await Promise.all(workers);
}

export type AiUrlImportDrawerProps = {
  open: boolean;
  onClose: () => void;
  heading?: string;
  /** Ladezustand im urlOnly-Modus (extern gesteuert). */
  isLoading?: boolean;
  /** Callback im urlOnly-Modus (Food-/PurchaseEditor). */
  onAnalyze?: (url: string) => void;
  /**
   * `dishImport` aktiviert den mehrstufigen Rezept-Import
   * (Quelle → Analyse → Mapping-Vorschau → Gericht erzeugen).
   */
  mode?: "urlOnly" | "dishImport";
  /** Quelle im dishImport-Modus. */
  source?: "url" | "text" | "image";
  /** Nur bei `source="image"`: die bereits ausgewählte Bilddatei. */
  imageFile?: File;
  onImported?: (dish: DishModification) => void;
};

/**
 * Drawer zur Eingabe einer URL, deren Inhalt anschließend per KI ausgewertet
 * wird. Im `urlOnly`-Modus wird nur die URL an `onAnalyze` gereicht (Food- und
 * PurchaseEditor). Im `dishImport`-Modus wird das Rezept extrahiert, die
 * Zutaten vom Nutzer auf interne Foods gemappt und anschließend über
 * `convertToDish` in ein Gericht überführt.
 */
export const AiUrlImportDrawer = ({
  open,
  onClose,
  heading = "KI-Import URL",
  isLoading = false,
  onAnalyze,
  mode = "urlOnly",
  source = "url",
  imageFile,
  onImported,
}: AiUrlImportDrawerProps) => {
  const isDishImport = mode === "dishImport";
  const toast = useToast();
  const drawerHistoryContext = useContext(DrawerHistoryContext);
  const searchDisclosure = usePlan2PlateDisclosure(drawerHistoryContext);

  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [step, setStep] = useState<DishImportStep>("input");
  const [description, setDescription] = useState("");
  const [portions, setPortions] = useState(1);
  const [mappings, setMappings] = useState<IngredientMapping[]>([]);
  const [extractedSteps, setExtractedSteps] = useState<string[]>([]);
  const [activeMappingIndex, setActiveMappingIndex] = useState<
    number | undefined
  >(undefined);
  const [editingFoodIndex, setEditingFoodIndex] = useState<number | undefined>(
    undefined,
  );
  const [isConverting, setIsConverting] = useState(false);
  const suggestionRunRef = useRef(0);
  const editFoodDisclosure = usePlan2PlateDisclosure(drawerHistoryContext);

  const reset = () => {
    suggestionRunRef.current += 1;
    setUrl("");
    setText("");
    setStep("input");
    setDescription("");
    setPortions(1);
    setMappings([]);
    setExtractedSteps([]);
    setActiveMappingIndex(undefined);
    setEditingFoodIndex(undefined);
  };

  const analyzing = isLoading || step === "loading";

  const applyExtraction = (data: ExtractIngredientsResponseListDto) => {
    setDescription(data.description ?? "");
    setPortions(data.portions && data.portions > 0 ? data.portions : 1);
    setExtractedSteps(
      (data.steps ?? [])
        .map((s) => s.text?.trim())
        .filter((t): t is string => !!t),
    );
    setMappings(
      (data.ingredients ?? []).map((ingredient) => ({
        extracted: ingredient,
        amount: ingredient.quantityNumeric ?? 0,
        ignored: false,
      })),
    );
    setStep("preview");
    void loadSuggestions(data.ingredients ?? []);
  };

  const runExtraction = async (
    load: () => Promise<{ data: ExtractIngredientsResponseListDto }>,
    errorTitle: string,
  ) => {
    setStep("loading");
    try {
      applyExtraction((await load()).data);
    } catch {
      toast({
        title: errorTitle,
        status: "error",
        isClosable: true,
      });
      setStep("input");
    }
  };

  const handleAnalyze = async () => {
    if (analyzing) return;
    if (!isDishImport) {
      if (!url.trim()) return;
      onAnalyze?.(url);
      return;
    }
    if (source === "text") {
      if (!text.trim()) return;
      await runExtraction(
        () => getIngredientsFromText({ body: text }),
        "Rezept konnte aus dem Text nicht geladen werden",
      );
      return;
    }
    if (!url.trim()) return;
    await runExtraction(
      () => getIngredients({ body: url.trim() }),
      "Rezept konnte nicht aus der URL geladen werden",
    );
  };

  // Bild-Import: sobald der Drawer mit einer Datei geöffnet wird, direkt extrahieren.
  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    if (isDishImport && source === "image" && imageFile) {
      void runExtraction(
        () => getIngredientsFromImage({ body: { file: imageFile } }),
        "Rezept konnte aus dem Foto nicht geladen werden",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source, imageFile]);

  const openMappingSearch = (index: number) => {
    setActiveMappingIndex(index);
    searchDisclosure.onOpen();
  };

  /**
   * Übernimmt ein internes Food samt passender Einheit/Menge in die Zeile.
   * Ein vom Backend vorgeschlagenes Unit/Amount-Preset wird bevorzugt; ohne
   * (oder mit ungültiger) Preset-Einheit greift die lokale Heuristik.
   */
  const applyFood = (
    index: number,
    food: Food,
    preset?: { foodUnitId?: number; amount?: number },
  ) => {
    setMappings((prev) =>
      prev.map((m, i) => {
        if (i !== index) return m;
        const presetUnit =
          preset?.foodUnitId != null &&
          preset?.amount != null &&
          food.foodUnits.some((u) => u.id === preset.foodUnitId)
            ? { foodUnitId: preset.foodUnitId, amount: preset.amount }
            : undefined;
        const { foodUnit, amount } = matchExtractedIngredient(
          food,
          m.extracted,
        );
        return {
          ...m,
          food,
          foodUnitId: presetUnit?.foodUnitId ?? foodUnit?.id,
          amount: presetUnit?.amount ?? amount,
          ignored: false,
        };
      }),
    );
  };

  const handleSelectFood = (food: Food) => {
    if (activeMappingIndex == null) return;
    applyFood(activeMappingIndex, food);
  };

  const applySuggestion = (index: number) => {
    const mapping = mappings[index];
    const suggestedFood = mapping?.suggestedFood;
    if (!suggestedFood) return;
    applyFood(index, suggestedFood, {
      foodUnitId: mapping.suggestedFoodUnitId,
      amount: mapping.suggestedAmount,
    });
  };

  /**
   * Holt für jede Zutat asynchron einen internen Food-Vorschlag – inkl.
   * Vorschlag für Einheit und umgerechnete Menge, falls die extrahierte
   * Zutat eine Menge/Einheit mitbringt.
   */
  const loadSuggestions = async (
    ingredients: ExtractIngredientsResponseDto[],
  ) => {
    if (!ingredients.length) return;
    suggestionRunRef.current += 1;
    const runId = suggestionRunRef.current;
    setMappings((prev) => prev.map((m) => ({ ...m, suggestionLoading: true })));
    await runWithConcurrency(
      ingredients,
      SUGGESTION_CONCURRENCY,
      async (ingredient, index) => {
        try {
          const response = await suggestFood({
            body: {
              name: ingredient.name,
              aliases: ingredient.aliases,
              quantityNumeric: ingredient.quantityNumeric,
              unit: ingredient.unit,
            },
          });
          if (runId !== suggestionRunRef.current) return;
          const suggestedFood = response.data.food as Food | undefined;
          setMappings((prev) =>
            prev.map((m, i) =>
              i === index
                ? {
                    ...m,
                    suggestionLoading: false,
                    suggestedFood,
                    suggestionSource: response.data.source,
                    suggestedFoodUnitId: response.data.foodUnitId,
                    suggestedAmount: response.data.amount,
                  }
                : m,
            ),
          );
        } catch {
          if (runId !== suggestionRunRef.current) return;
          setMappings((prev) =>
            prev.map((m, i) =>
              i === index ? { ...m, suggestionLoading: false } : m,
            ),
          );
        }
      },
    );
  };

  const updateAmount = (index: number, amount: number) => {
    setMappings((prev) =>
      prev.map((m, i) => (i === index ? { ...m, amount } : m)),
    );
  };

  const updateUnit = (index: number, unit: FoodUnit | undefined) => {
    setMappings((prev) =>
      prev.map((m, i) => (i === index ? { ...m, foodUnitId: unit?.id } : m)),
    );
  };

  const openFoodEdit = (index: number) => {
    setEditingFoodIndex(index);
    editFoodDisclosure.onOpen();
  };

  /**
   * Zieht das in der FoodEditor-Drawer gespeicherte, bearbeitete Food in die
   * Mapping-Zeile. Der Food-/Purchase-Cache wird vom FoodEditor beim Speichern
   * bereits invalidiert (`invalidateFoods`); hier muss nur das lokale Snapshot
   * des Drawers erneuert und eine ggf. gelöschte Einheit neu bestimmt werden.
   */
  const handleFoodEdited = (food: Food) => {
    const index = editingFoodIndex;
    if (index == null) return;
    setMappings((prev) =>
      prev.map((m, i) => {
        if (i !== index) return m;
        const keepUnit =
          m.foodUnitId != null &&
          food.foodUnits.some((u) => u.id === m.foodUnitId);
        const { foodUnit, amount } = keepUnit
          ? { foodUnit: undefined, amount: m.amount }
          : matchExtractedIngredient(food, m.extracted);
        return {
          ...m,
          food,
          foodUnitId: keepUnit ? m.foodUnitId : foodUnit?.id,
          amount,
          suggestedFood:
            m.suggestedFood?.id === food.id ? food : m.suggestedFood,
        };
      }),
    );
  };

  const toggleIgnored = (index: number) => {
    setMappings((prev) =>
      prev.map((m, i) => (i === index ? { ...m, ignored: !m.ignored } : m)),
    );
  };

  const assignedCount = mappings.filter(
    (m) => !m.ignored && m.foodUnitId != null,
  ).length;
  const allAssigned = mappings.every((m) => m.ignored || m.foodUnitId != null);

  const handleConvert = async () => {
    if (!allAssigned || isConverting) return;
    setIsConverting(true);
    try {
      const ingredients: StepIngredientCreateDto[] = [];
      const ingredientMapping: IngredientMappingDto[] = [];
      const validIds = new Set<number>();
      mappings.forEach((m) => {
        if (m.ignored || m.foodUnitId == null) return;
        ingredients.push({ foodUnitId: m.foodUnitId, amount: m.amount });
        const selectedUnit = m.food?.foodUnits.find(
          (f) => f.id === m.foodUnitId,
        );
        ingredientMapping.push({
          name: m.extracted.name,
          foodUnitId: m.foodUnitId,
          unitName: selectedUnit?.description,
          amount: m.amount,
        });
        validIds.add(m.foodUnitId);
      });

      // Die bereits angezeigten Schritte und Mengen verwenden; das Backend
      // verteilt nur noch die Zutaten prozentual auf die Schritte.
      const resolvedDescription = description.trim() || "Importiertes Rezept";

      const response = await convertToDish({
        body: {
          description: resolvedDescription,
          purchaseRelevant: true,
          vendor: "",
          ingredients,
          ingredientMapping,
          portions,
          steps: extractedSteps,
        },
      });
      const dish = response.data.dish;
      if (!dish) throw new Error("Kein Gericht erhalten");
      // Die Portionen übernimmt der DishEditor als recipePortions (Skalierung
      // pro Portion <-> Gesamtmenge passiert dort).
      onImported?.({
        ...sanitizeDish(dish, validIds),
        recipePortions: dish.portions ?? portions,
      });
      reset();
    } catch {
      toast({
        title: "Gericht konnte nicht erstellt werden",
        status: "error",
        isClosable: true,
      });
    } finally {
      setIsConverting(false);
    }
  };

  const previewActive = isDishImport && step === "preview";
  const inputHeading =
    source === "text"
      ? "Rezept aus Text"
      : source === "image"
        ? "Rezept aus Foto"
        : heading;

  return (
    <Plan2PlateDrawer
      open={open}
      onClose={onClose}
      header={
        <Plan2PlateDrawerHeader
          heading={previewActive ? "Rezept importieren" : inputHeading}
        />
      }
      body={
        <>
          {isDishImport && (
            <SearchForFoodDrawer
              open={searchDisclosure.isOpen}
              onClose={searchDisclosure.onClose}
              selectFood={handleSelectFood}
              initialSearchTerm={
                activeMappingIndex != null
                  ? mappings[activeMappingIndex]?.extracted.name
                  : undefined
              }
            />
          )}
          {previewActive ? (
            <div className="flex w-full flex-col gap-4">
              <EditFoodDrawer
                open={editFoodDisclosure.isOpen}
                onClose={editFoodDisclosure.onClose}
                allowUnitDeletion={false}
                food={
                  editingFoodIndex != null
                    ? mappings[editingFoodIndex]?.food
                    : undefined
                }
                onSubmit={handleFoodEdited}
              />

              <Input
                data-cy="ai-url-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Bezeichnung"
                className="h-11 px-4 text-base"
              />

              <Card className="flex items-center justify-between gap-2 p-3">
                <SectionLabel noMargin className="flex-1">
                  Portionen (Rezept gesamt)
                </SectionLabel>
                <Counter
                  dataCy="ai-url-portions"
                  value={portions}
                  onChange={setPortions}
                  min={1}
                  max={24}
                />
              </Card>

              <div>
                <div className="flex items-center justify-between">
                  <SectionLabel noMargin>Zutaten</SectionLabel>
                  <span
                    className="text-xs text-ink-muted"
                    data-cy="ai-url-mapping-progress"
                  >
                    {assignedCount} von {mappings.length} zugeordnet
                  </span>
                </div>
                <div className="mt-2 flex flex-col gap-2">
                  {mappings.map((mapping, index) => {
                    const isMapped =
                      !mapping.ignored && mapping.foodUnitId != null;
                    const selectedUnit = mapping.food?.foodUnits.find(
                      (u) => u.id === mapping.foodUnitId,
                    );
                    const quantity = formatExtractedQuantity(mapping.extracted);
                    return (
                      <Card
                        key={index}
                        data-cy={`ai-url-mapping-card-${index}`}
                        className={cn(
                          "p-3",
                          mapping.ignored && "opacity-50",
                          !mapping.ignored && !isMapped && "border-gold",
                        )}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-baseline gap-x-1.5">
                              <span className="truncate text-base font-semibold text-ink">
                                {mapping.extracted.name ?? "Unbenannte Zutat"}
                              </span>
                              {quantity && (
                                <span className="text-xs text-ink-muted">
                                  · {quantity}
                                </span>
                              )}
                            </div>
                            {mapping.extracted.aliases &&
                              mapping.extracted.aliases.length > 0 && (
                                <p className="truncate text-xs text-ink-muted">
                                  Auch: {mapping.extracted.aliases.join(", ")}
                                </p>
                              )}
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            {mapping.ignored ? (
                              <SmallIconButton
                                icon="plus"
                                label="Zutat wieder aufnehmen"
                                data-cy={`ai-url-mapping-ignore-${index}`}
                                onClick={() => toggleIgnored(index)}
                              />
                            ) : (
                              <>
                                {!isMapped && (
                                  <SmallIconButton
                                    icon="search"
                                    label="Zutat zuordnen"
                                    data-cy={`ai-url-mapping-select-${index}`}
                                    onClick={() => openMappingSearch(index)}
                                  />
                                )}
                                <SmallIconButton
                                  icon="delete"
                                  variant="destructive"
                                  label="Zutat ignorieren"
                                  data-cy={`ai-url-mapping-ignore-${index}`}
                                  onClick={() => toggleIgnored(index)}
                                />
                              </>
                            )}
                          </div>
                        </div>

                        {mapping.ignored && (
                          <div className="mt-1 text-sm text-ink-muted">
                            Wird ignoriert
                          </div>
                        )}

                        {!mapping.ignored && !isMapped && (
                          <>
                            {mapping.suggestionLoading ? (
                              <LookupSkeleton
                                className="mt-2"
                                dataCy={`ai-url-suggestion-loading-${index}`}
                              />
                            ) : mapping.suggestedFood ? (
                              <div className="mt-2 flex items-center justify-between gap-2 rounded-item bg-gold/10 pl-3">
                                <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5">
                                  <span className="min-w-0 text-sm font-semibold text-ink">
                                    {formatFoodDescription(
                                      mapping.suggestedFood,
                                    )}
                                  </span>
                                  {mapping.suggestedAmount != null &&
                                    mapping.suggestedFoodUnitId != null && (
                                      <span className="text-xs text-ink-muted">
                                        ·{" "}
                                        {formatSuggestedAmount(
                                          mapping.suggestedAmount,
                                          mapping.suggestedFood.foodUnits.find(
                                            (u) =>
                                              u.id ===
                                              mapping.suggestedFoodUnitId,
                                          )?.description,
                                        )}
                                      </span>
                                    )}
                                </div>
                                <SmallIconButton
                                  icon="check"
                                  label="Vorschlag übernehmen"
                                  className="shrink-0 rounded-full border-teal bg-transparent text-teal"
                                  data-cy={`ai-url-suggestion-apply-${index}`}
                                  onClick={() => applySuggestion(index)}
                                />
                              </div>
                            ) : (
                              <div className="mt-1 text-sm text-gold">
                                Noch zuordnen
                              </div>
                            )}
                          </>
                        )}

                        {isMapped && (
                          <>
                            <div className="mt-3 border-t border-line" />
                            <div className="mt-2 flex items-center justify-between gap-2">
                              <span className="min-w-0 truncate text-sm font-semibold text-ink">
                                {formatFoodDescription(mapping.food)}
                              </span>
                              <div className="flex shrink-0 items-center gap-2">
                                <SmallIconButton
                                  icon="reassign"
                                  label="Zutat neu zuordnen"
                                  className="shrink-0"
                                  data-cy={`ai-url-mapping-select-${index}`}
                                  onClick={() => openMappingSearch(index)}
                                />
                                <SmallIconButton
                                  icon="pencil"
                                  label="Lebensmittel bearbeiten"
                                  className="shrink-0"
                                  data-cy={`ai-url-mapping-edit-${index}`}
                                  onClick={() => openFoodEdit(index)}
                                />
                              </div>
                            </div>
                            <UnitAmount
                              className="mt-2"
                              dataCy={`ai-url-mapping-amount-${index}`}
                              foodUnit={selectedUnit}
                              units={mapping.food?.foodUnits ?? []}
                              amount={mapping.amount}
                              onUnitChange={(unit) => updateUnit(index, unit)}
                              onAmountChange={(value) =>
                                updateAmount(index, value)
                              }
                            />
                          </>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>

              {extractedSteps.length > 0 && (
                <div>
                  <SectionLabel>Zubereitung</SectionLabel>
                  <ol className="flex flex-col gap-2">
                    {extractedSteps.map((stepText, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-2 text-sm text-ink"
                      >
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal/10 text-2xs font-semibold text-teal">
                          {index + 1}
                        </span>
                        <span>{stepText}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {!allAssigned && (
                <p
                  className="text-xs text-gold"
                  data-cy="ai-url-mapping-incomplete"
                >
                  Bitte alle Zutaten zuordnen oder ignorieren.
                </p>
              )}

              <Button
                dataCy="ai-url-submit-button"
                fullWidth
                disabled={!allAssigned || isConverting}
                onClick={handleConvert}
                size="lg"
                variant={allAssigned ? "primary" : "tertiary"}
              >
                {isConverting ? "Importiere …" : "Gericht importieren"}
              </Button>
            </div>
          ) : (
            <div className="flex w-full flex-col gap-4 p-4">
              {source === "url" && (
                <Input
                  data-cy="ai-url-input"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.chefkoch.de/..."
                  className="border-line bg-surface-card"
                  type="url"
                  disabled={analyzing}
                />
              )}
              {source === "text" && (
                <Textarea
                  data-cy="ai-text-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={
                    "Rezept einfügen...\n\nz.B.:\n3 Eier, 30g Feta, 60g Champignons\n1. Pilze anbraten\n2. Eier drüber"
                  }
                  className="min-h-[220px] resize-none border-line bg-surface-card"
                  disabled={analyzing}
                />
              )}
              {source === "image" && !analyzing && (
                <span className="flex items-center gap-2 text-sm text-ink-muted">
                  Das Foto konnte nicht ausgewertet werden. Bitte erneut
                  auswählen.
                </span>
              )}
              {analyzing && source !== "image" && (
                <span className="flex items-center gap-2 text-sm text-ink-muted">
                  <Spinner />
                  {source === "text"
                    ? "Der Text wird analysiert …"
                    : "Die Seite wird analysiert …"}
                </span>
              )}
              {analyzing && source === "image" && (
                <span className="flex items-center gap-2 text-sm text-ink-muted">
                  <Spinner />
                  Das Foto wird analysiert …
                </span>
              )}
            </div>
          )}
        </>
      }
      footer={
        // Kein Footer im Preview: der Import-Button steht am Ende des Contents
        // und wird erst nach dem Scrollen erreicht.
        previewActive || (isDishImport && source === "image") ? undefined : (
          <div className="flex w-full items-center justify-center p-4 pt-2">
            <Button
              dataCy="ai-url-analyze-button"
              fullWidth
              disabled={
                analyzing || (source === "text" ? !text.trim() : !url.trim())
              }
              onClick={handleAnalyze}
              size="lg"
              variant="primary"
            >
              {analyzing ? "Analysiere …" : "Analysieren"}
            </Button>
          </div>
        )
      }
    />
  );
};

/** Formatiert die von der KI vorgeschlagene Menge inkl. Einheit für die Anzeige. */
function formatSuggestedAmount(amount: number, unitDescription?: string) {
  const value = Number.isInteger(amount)
    ? String(amount)
    : String(Math.round(amount * 10) / 10);
  return [value, unitDescription].filter(Boolean).join(" ");
}

/** Entfernt ungültige/erfundene Zutaten und füllt fehlende Pflichtfelder auf. */
function sanitizeDish(
  dish: DishModificationDto,
  validIds: Set<number>,
): DishModification {
  return {
    ...dish,
    tags: dish.tags ?? [],
    steps: (dish.steps ?? []).map((step) => ({
      ...step,
      ingredients: (step.ingredients ?? [])
        .filter((ing) => ing.foodUnitId != null && validIds.has(ing.foodUnitId))
        .map((ing) => ({
          ...ing,
          alternatives: ing.alternatives ?? [],
          personChoices: ing.personChoices ?? [],
        })),
    })),
  };
}
